import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Trophy, RotateCcw, TrendingUp, Share2, Check, Loader2 } from 'lucide-react';
import { GameState, PlayerRoundResult } from '../types';
import { cn } from '../lib/utils';
import { useSettings } from './SettingsContext';
import { createGameResultShareUrl, emitProductEvent, gameResultShareEvent } from '../lib/productAnalytics';

interface RoundSummaryProps {
  gameState: GameState;
  roundResults: PlayerRoundResult[];
  onNextRound: () => void;
  onRestart: () => void;
  onExit?: () => void;
  isOnline?: boolean;
}

export default function RoundSummary({ 
  gameState, 
  roundResults, 
  onNextRound, 
  onRestart,
  onExit,
  isOnline = false,
}: RoundSummaryProps) {
  const { playSfx, triggerHaptic } = useSettings();
  const numPlayers = gameState.numPlayers;
  const colsCount = numPlayers === 4 ? 2 : numPlayers;
  const [timeLeft, setTimeLeft] = useState<number>(30);
  const [shareState, setShareState] = useState<'idle' | 'sharing' | 'shared' | 'error'>('idle');
  
  const isGameOver = gameState.phase === 'gameOver';
  const playerWon = gameState.players[0].totalScore > gameState.players[1].totalScore;
  const isWinStyle = !isGameOver || playerWon;
  const playerScore = gameState.players[0]?.totalScore ?? 0;
  const opponentScore = gameState.players[1]?.totalScore ?? 0;
  const result = playerScore === opponentScore ? 'draw' : playerWon ? 'win' : 'loss';

  const shareResult = async () => {
    if (!isGameOver || isOnline || shareState === 'sharing') return;

    setShareState('sharing');
    const shareUrl = createGameResultShareUrl(window.location.origin);
    const shareText = result === 'win'
      ? `Ho vinto ${playerScore}-${opponentScore} a Sbarazzino. Riesci a battermi?`
      : result === 'draw'
        ? `Ho pareggiato ${playerScore}-${opponentScore} a Sbarazzino. Riesci a superarmi?`
        : `Ho fatto ${playerScore} punti a Sbarazzino. Riesci a battere il mio risultato?`;

    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({
          title: 'Sfida un amico a Sbarazzino',
          text: shareText,
          url: shareUrl,
        });
        emitProductEvent(gameResultShareEvent('native', result));
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        emitProductEvent(gameResultShareEvent('clipboard', result));
      } else {
        throw new Error('share_unavailable');
      }

      setShareState('shared');
      window.setTimeout(() => setShareState('idle'), 2500);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        setShareState('idle');
        return;
      }
      setShareState('error');
      window.setTimeout(() => setShareState('idle'), 2500);
    }
  };

  useEffect(() => {
    if (isGameOver) {
      if (playerWon) {
        playSfx('scopa');
        triggerHaptic('success');
      } else {
        playSfx('error');
        triggerHaptic('warning');
      }
    } else {
      playSfx('shuffle');
      triggerHaptic('medium');
    }
  }, [isGameOver, playerWon, playSfx, triggerHaptic]);

  useEffect(() => {
    if (!isOnline || gameState.phase !== 'roundEnd' || !gameState.roundEndedAt) {
      return;
    }

    const calculateTimeLeft = () => {
      const elapsed = Date.now() - (gameState.roundEndedAt || 0);
      const remaining = Math.max(0, 30 - Math.floor(elapsed / 1000));
      return remaining;
    };

    // Set initial time
    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        onNextRound();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isOnline, gameState.phase, gameState.roundEndedAt, onNextRound]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md overflow-y-auto"
    >
      <div className="min-h-full flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          className={cn(
            "p-6 rounded-3xl max-w-lg w-full my-8 text-center space-y-6 border-4",
            isWinStyle 
              ? "glass-panel-amber active-glow-panel border-yellow-500/30 shadow-[0_0_100px_rgba(251,191,36,0.15)]" 
              : "glass-panel-rose border-rose-500/30 shadow-[0_0_100px_rgba(244,63,94,0.15)]"
          )}
        >
        <div className="flex justify-center">
          {gameState.phase === 'gameOver' ? (
            <div className="w-24 h-24 bg-yellow-500 rounded-full flex items-center justify-center shadow-[0_0_50px_rgba(234,179,8,0.5)]">
              <Trophy className="w-12 h-12 text-black" />
            </div>
          ) : (
            <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-500/50">
              <RotateCcw className="w-10 h-10 text-blue-400" />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <h2 className="text-3xl sm:text-4xl font-black italic uppercase tracking-tighter text-white">
            {gameState.phase === 'gameOver' ? 'Partita Finita!' : 'Round Terminato'}
          </h2>
          <p className="text-xl text-white/60">
            {gameState.phase === 'gameOver' 
              ? (gameState.players[0].totalScore > gameState.players[1].totalScore 
                  ? (numPlayers === 4 ? 'La tua squadra ha vinto!' : 'Hai Vinto!') 
                  : (isOnline 
                      ? 'Hanno vinto gli avversari.' 
                      : (numPlayers === 4 ? 'Hanno vinto gli avversari.' : 'La CPU ha vinto.')))
              : 'Punteggi del round'}
          </p>
        </div>

        <div className="grid gap-6 pt-6 pb-4 border-t border-white/10" style={{ gridTemplateColumns: `repeat(${colsCount}, 1fr)` }}>
          {Array.from({ length: colsCount }).map((_, i) => {
            const teamPlayers = numPlayers === 4
              ? (i === 0 ? [gameState.players[0], gameState.players[2]] : [gameState.players[1], gameState.players[3]])
              : [gameState.players[i]];
            const mainPlayer = teamPlayers[0];

            return (
              <div key={i} className={`space-y-4 ${i > 0 ? 'border-l border-white/10 pl-6' : ''}`}>
                <div className="flex flex-col items-center gap-1 min-h-[40px] justify-center">
                  {teamPlayers.map((p, pIdx) => (
                    <React.Fragment key={p.id}>
                      {pIdx > 0 && <span className="text-[10px] text-white/40 font-bold -my-1">&</span>}
                      <div className="text-xs uppercase tracking-widest text-white/60 font-semibold flex items-center justify-center gap-1.5">
                        {p.name}
                        {isOnline && gameState.phase === 'roundEnd' && (
                          <span 
                            className={cn(
                              "w-2.5 h-2.5 rounded-full inline-block transition-all duration-300",
                              p.readyForNextRound 
                                ? "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]" 
                                : "bg-white/20"
                            )} 
                            title={p.readyForNextRound ? 'Pronto' : 'In attesa'} 
                          />
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div className="text-5xl sm:text-6xl font-black" style={{ fontFamily: '"Playfair Display", serif', color: i === 0 ? '#fbbf24' : '#f87171', textShadow: i === 0 ? '0 0 20px rgba(251,191,36,0.4)' : '0 0 20px rgba(248,113,113,0.4)' }}>{mainPlayer.totalScore}</div>
                <div className="text-sm font-bold text-green-400 flex items-center justify-center gap-1">
                  <TrendingUp className="w-4 h-4" /> +{mainPlayer.currentRoundScore}
                </div>
              </div>
            );
          })}
        </div>

        {roundResults && (
          <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 text-xs sm:text-sm text-left border border-white/10 space-y-2 max-h-48 overflow-y-auto no-scrollbar">
            <div className="grid gap-2 font-bold text-white/50 uppercase tracking-wider mb-3" style={{ gridTemplateColumns: `2fr repeat(${colsCount}, 1fr)` }}>
              <div>Punto</div>
              {Array.from({ length: colsCount }).map((_, i) => {
                const label = numPlayers === 4 
                  ? (i === 0 ? 'Noi' : 'Avversari')
                  : gameState.players[i]?.name;
                const subLabel = numPlayers === 4
                  ? (i === 0 
                      ? `${gameState.players[0].name.replace(' (Tu)', '')} & ${gameState.players[2].name}`
                      : `${gameState.players[1].name} & ${gameState.players[3].name}`)
                  : null;
                return (
                  <div key={i} className="text-center flex flex-col items-center">
                    <span className="leading-tight">{label}</span>
                    {subLabel && <span className="text-[9px] text-white/30 lowercase font-normal leading-none mt-0.5">{subLabel}</span>}
                  </div>
                );
              })}
            </div>

            <div className="grid gap-2 items-center" style={{ gridTemplateColumns: `2fr repeat(${colsCount}, 1fr)` }}>
              <div className="text-white/70">Carte (16 per pt)</div>
              {Array.from({ length: colsCount }).map((_, i) => (
                <div key={i} className={cn("text-center", (roundResults?.[i]?.cards ?? 0) > 20 ? "text-yellow-400 font-bold" : "text-white/50")}>{roundResults?.[i]?.cards ?? 0}</div>
              ))}
            </div>

            <div className="grid gap-2 items-center" style={{ gridTemplateColumns: `2fr repeat(${colsCount}, 1fr)` }}>
              <div className="text-white/70">Denari (6 per pt)</div>
              {Array.from({ length: colsCount }).map((_, i) => (
                <div key={i} className={cn("text-center", (roundResults?.[i]?.coins ?? 0) > 5 ? "text-yellow-400 font-bold" : "text-white/50")}>{roundResults?.[i]?.coins ?? 0}</div>
              ))}
            </div>

            <div className="grid gap-2 items-center" style={{ gridTemplateColumns: `2fr repeat(${colsCount}, 1fr)` }}>
              <div className="text-white/70">Settebello (1 pt)</div>
              {Array.from({ length: colsCount }).map((_, i) => (
                <div key={i} className={cn("text-center", roundResults?.[i]?.settebello ? "text-yellow-400 font-bold" : "text-white/50")}>{roundResults?.[i]?.settebello ? '✅' : '❌'}</div>
              ))}
            </div>

            <div className="grid gap-2 items-center" style={{ gridTemplateColumns: `2fr repeat(${colsCount}, 1fr)` }}>
              <div className="text-white/70">Primiera (1 pt)</div>
              {Array.from({ length: colsCount }).map((_, i) => {
                const val = roundResults?.[i]?.primiera ?? 0;
                const maxVal = Math.max(...Array.from({ length: colsCount }).map((_, j) => roundResults?.[j]?.primiera ?? 0));
                return <div key={i} className={cn("text-center", val === maxVal && maxVal > 0 ? "text-yellow-400 font-bold" : "text-white/50")}>{val}</div>;
              })}
            </div>

            <div className="grid gap-2 items-center" style={{ gridTemplateColumns: `2fr repeat(${colsCount}, 1fr)` }}>
              <div className="text-white/70">Scope (1 pt cad)</div>
              {Array.from({ length: colsCount }).map((_, i) => (
                <div key={i} className={cn("text-center", (roundResults?.[i]?.scope ?? 0) > 0 ? "text-yellow-400 font-bold" : "text-white/50")}>{roundResults?.[i]?.scope ?? 0}</div>
              ))}
            </div>

            {roundResults?.some((_, i) => (roundResults?.[i]?.lowHand ?? 0) > 0) && (
              <div className="grid gap-2 items-center" style={{ gridTemplateColumns: `2fr repeat(${colsCount}, 1fr)` }}>
                <div className="text-white/70">Sbarazzo (+3 pt)</div>
                {Array.from({ length: colsCount }).map((_, i) => (
                  <div key={i} className={cn("text-center", (roundResults?.[i]?.lowHand ?? 0) > 0 ? "text-yellow-400 font-bold" : "text-white/50")}>{roundResults?.[i]?.lowHand ?? 0}</div>
                ))}
              </div>
            )}

            {roundResults?.some((_, i) => (roundResults?.[i]?.threeAces ?? 0) > 0) && (
              <div className="grid gap-2 items-center" style={{ gridTemplateColumns: `2fr repeat(${colsCount}, 1fr)` }}>
                <div className="text-white/70">Tre Assi (+6 pt)</div>
                {Array.from({ length: colsCount }).map((_, i) => (
                  <div key={i} className={cn("text-center", (roundResults?.[i]?.threeAces ?? 0) > 0 ? "text-yellow-400 font-bold" : "text-white/50")}>{roundResults?.[i]?.threeAces ?? 0}</div>
                ))}
              </div>
            )}

            {roundResults?.some((_, i) => roundResults?.[i]?.dieciDenari) && (
              <div className="grid gap-2 items-center" style={{ gridTemplateColumns: `2fr repeat(${colsCount}, 1fr)` }}>
                <div className="text-white/70">Re di Denari (+1 pt)</div>
                {Array.from({ length: colsCount }).map((_, i) => (
                  <div key={i} className={cn("text-center", roundResults?.[i]?.dieciDenari ? "text-yellow-400 font-bold" : "text-white/50")}>{roundResults?.[i]?.dieciDenari ? '1' : '-'}</div>
                ))}
              </div>
            )}

            {roundResults?.some((_, i) => roundResults?.[i]?.dueDenari) && (
              <div className="grid gap-2 items-center" style={{ gridTemplateColumns: `2fr repeat(${colsCount}, 1fr)` }}>
                <div className="text-white/70">Due di Denari (-5 pt)</div>
                {Array.from({ length: colsCount }).map((_, i) => (
                  <div key={i} className={cn("text-center", roundResults?.[i]?.dueDenari ? "text-red-400 font-bold" : "text-white/50")}>{roundResults?.[i]?.dueDenari ? '❌ (-5)' : '-'}</div>
                ))}
              </div>
            )}

            <div className="mt-2 text-center text-[10px] text-white/40 italic">
              Il giocatore senza carte di denari prende -10 pt (se ha effettuato prese).
            </div>
          </div>
        )}

        {isOnline && gameState.phase === 'roundEnd' && (
          <div className="text-white/60 text-xs sm:text-sm font-semibold animate-pulse pb-2">
            Il gioco riprenderà automaticamente tra {timeLeft}s
          </div>
        )}

        {gameState.phase === 'gameOver' ? (
          <div className="flex flex-col gap-2.5 w-full">
            {!isOnline && (
              <button
                onClick={shareResult}
                disabled={shareState === 'sharing'}
                className={cn(
                  "w-full py-4 border font-black uppercase text-sm tracking-wider rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2",
                  shareState === 'shared'
                    ? "bg-green-500/15 border-green-400/40 text-green-300"
                    : shareState === 'error'
                      ? "bg-red-500/10 border-red-400/30 text-red-300"
                      : "bg-white/5 hover:bg-white/10 border-white/10 text-white/85",
                  shareState === 'sharing' && "opacity-70 cursor-wait",
                )}
              >
                {shareState === 'sharing' ? <Loader2 className="w-4 h-4 animate-spin" /> : shareState === 'shared' ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                {shareState === 'shared' ? 'Risultato condiviso' : shareState === 'error' ? 'Condivisione non disponibile' : 'Sfida un amico'}
              </button>
            )}
            <button
              onClick={() => {
                playSfx('click');
                triggerHaptic('light');
                onRestart();
              }}
              className="w-full py-5 bg-yellow-500 hover:bg-yellow-400 text-black font-black uppercase text-base tracking-[0.25em] rounded-2xl shadow-xl shadow-yellow-500/10 transition-all active:scale-95"
            >
              Gioca Ancora
            </button>
            <button
              onClick={() => {
                playSfx('click');
                triggerHaptic('light');
                onExit?.();
              }}
              className="w-full py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 font-bold uppercase text-sm tracking-wider rounded-2xl transition-all active:scale-95"
            >
              Torna al Menu
            </button>
          </div>
        ) : (
          <button
            disabled={isOnline && gameState.phase === 'roundEnd' && (gameState.players[0]?.readyForNextRound || false)}
            onClick={() => {
              playSfx('click');
              triggerHaptic('light');
              onNextRound();
            }}
            className={cn(
              "w-full py-6 text-black font-black uppercase text-lg tracking-[0.3em] rounded-2xl shadow-xl transition-all active:scale-95",
              isOnline && gameState.phase === 'roundEnd' && (gameState.players[0]?.readyForNextRound || false)
                ? "bg-green-600 hover:bg-green-600 text-white/80 cursor-not-allowed shadow-green-600/20"
                : "bg-yellow-500 hover:bg-yellow-400 shadow-yellow-500/20"
            )}
          >
            {isOnline && gameState.phase === 'roundEnd'
              ? gameState.players[0]?.readyForNextRound
                ? `Pronto (${gameState.players.filter(p => p.readyForNextRound).length}/${numPlayers})`
                : `Continua (${gameState.players.filter(p => p.readyForNextRound).length}/${numPlayers})`
              : 'Continua'}
          </button>
        )}
        </motion.div>
      </div>
    </motion.div>
  );
}
